import { supabase } from "@/integrations/supabase/client";
import { highestRole, type StaffRole } from "@/lib/staff-roles";

/**
 * Grants the signed-in user the admin role when their email is on the
 * server-side approved list (public.admin_allowlist). The check runs entirely
 * in the database, so nothing here can be spoofed by the client.
 *
 * Returns true when the user holds the admin role afterwards.
 */
export async function claimAdminRole(): Promise<boolean> {
  const client = supabase as unknown as {
    rpc: (fn: string) => Promise<{ data: unknown; error: unknown }>;
  };
  try {
    const { data, error } = await client.rpc("claim_admin_role");
    if (error) return false;
    return data === true;
  } catch {
    return false;
  }
}

/**
 * Server-authoritative check (RLS-scoped read of public.user_roles) telling
 * whether the signed-in user currently holds the admin role.
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return false;
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (error) return false;
    return Boolean(data);
  } catch {
    return false;
  }
}

/**
 * The strongest staff role the signed-in user holds, or null when they are not
 * on the crew. Read through RLS, so it cannot be faked from the browser.
 */
export async function getCurrentStaffRole(): Promise<StaffRole | null> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return null;
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (error) return null;
    return highestRole((data ?? []).map((r) => String(r.role)));
  } catch {
    return null;
  }
}
